import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTenant } from '../Context/TenantContext';
import API_BASE_URL from '../Config/config';
import {
  AcademicCapIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

/**
 * TenantFooter — Production-grade, multi-tenant aware Student Portal footer.
 *
 * Layout (4-col → 2-col → 1-col responsive grid):
 * ┌──────────────┬─────────────┬──────────────┬──────────────────────┐
 * │  Branding    │ Quick Links │  Services    │  Contact & Social    │
 * └──────────────┴─────────────┴──────────────┴──────────────────────┘
 * ┌──────────────────── Copyright Strip ─────────────────────────────┐
 * └──────────────────────────────────────────────────────────────────┘
 *
 * All tenant-specific data is fetched from TenantContext — zero hardcoded values.
 */

/* ─── SVG Social Icons (inline — no extra dependency) ─── */
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

/* ─── Token constants ─── */
const COLORS = {
  bgMain:    '#843FEF',
  bgDeep:    '#6D32C9',
  bgCard:    'rgba(255,255,255,0.03)',
  bgCardHov: 'rgba(255,255,255,0.06)',
  border:    'rgba(255,255,255,0.07)',
  borderHov: 'rgba(255,255,255,0.15)',
  textPrim:  '#F1F5F9',
  textSec:   '#CBD5E1',
  textMute:  '#E2E8F0',
  textFaint: '#CBD5E1',
  accent:    '#FFFFFF',
  grad:      'linear-gradient(135deg, #843FEF 0%, #6D32C9 100%)',
};

/* ─── Social platform config ─── */
const SOCIAL_PLATFORMS = [
  {
    key: 'linkedin',
    label: 'LinkedIn',
    Icon: LinkedInIcon,
    hoverColor: '#0A66C2',
    ariaLabel: 'Visit our LinkedIn page',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    Icon: YouTubeIcon,
    hoverColor: '#FF0000',
    ariaLabel: 'Visit our YouTube channel',
  },
  {
    key: 'github',
    label: 'GitHub',
    Icon: GitHubIcon,
    hoverColor: '#333333',
    ariaLabel: 'Visit our GitHub organization',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    Icon: InstagramIcon,
    hoverColor: '#E1306C',
    ariaLabel: 'Visit our Instagram profile',
  },
];

/* ─── Navigation data ─── */
const QUICK_LINKS = [
  { label: 'Dashboard',       path: '/dashboard' },
  { label: 'My Profile',      path: '/profile' },
  { label: 'My Bootcamps',    path: '/bootcamp/my-bootcamps' },
  { label: 'Tech Explorer',   path: '/student/explore' },
  { label: 'Compiler',        path: '/compiler' },
  { label: 'Mock Interview',  path: '/student/mockinterviewhome' },
  { label: 'Launchpad',       path: '/student/launch' },
];

const STUDENT_SERVICES = [
  { label: 'AI Bootcamps',    path: '/bootcamp/my-bootcamps' },
  { label: 'Resume Builder',  path: '/student/tsg' },
  { label: 'Skill Rankings',  path: '/profile' },
  { label: 'Career Roadmap',  path: '/student/launch' },
  { label: 'Certifications',  path: '/bootcamp/my-bootcamps' },
];

const DEFAULT_DESCRIPTION =
  'An AI-powered career acceleration platform designed to bridge academic learning with industry-ready skills through bootcamps, mock interviews, coding practice, and personalized learning experiences.';

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const TenantFooter = () => {
  const {
    getCollegeName,
    getLogoUrl,
    getCollegePhone,
    getCollegeEmail,
    getCollegeWebsite,
    getCollegeLocation,
    getCollegeAddress,
    getCollegeDescription,
    getSocialLinks,
    tenant,
  } = useTenant();

  const navigate = useNavigate();
  const [logoError, setLogoError] = useState(false);

  const collegeName   = getCollegeName();
  const logoUrl       = getLogoUrl() || '/logo192.png';
  const phone         = getCollegePhone();
  const email         = getCollegeEmail();
  const website       = getCollegeWebsite();
  const address       = getCollegeAddress?.() || getCollegeLocation?.() || null;
  const description   = getCollegeDescription?.() || DEFAULT_DESCRIPTION;
  const socialLinks   = getSocialLinks?.() || {};
  const currentYear   = new Date().getFullYear();

  const showLogo        = logoUrl && !logoError;
  const activeSocials   = SOCIAL_PLATFORMS.filter(p => socialLinks[p.key]).map(p => {
    if (p.key === 'youtube') return { ...p, url: "https://www.youtube.com/@EvolveXInnovations" };
    return { ...p, url: socialLinks[p.key] };
  });
  const hasSocials      = activeSocials.length > 0;
  const hasContactInfo  = phone || email || website || address;

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <footer
      role="contentinfo"
      aria-label={`${collegeName} Student Portal footer`}
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}
    >
      {/* ── Gradient top border ── */}
      <div
        style={{
          height: '3px',
          background: 'linear-gradient(90deg, #3B82F6 0%, #6366F1 50%, #8B5CF6 100%)',
        }}
        aria-hidden="true"
      />

      {/* ══════════════════════════════════════════
          MAIN BODY
      ══════════════════════════════════════════ */}
      <div style={{ background: `linear-gradient(180deg, ${COLORS.bgMain} 0%, ${COLORS.bgDeep} 100%)` }}>
        <div className="tenant-footer-container">

          {/* ── Column 1: Branding ── */}
          <div className="tenant-footer-col tenant-footer-col--brand">

            {/* Logo + Name row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div
                className="tenant-footer-logo-wrap"
                title={collegeName}
              >
                {logoUrl && !logoError ? (
                  <img
                    src={logoUrl}
                    alt={`${collegeName} logo`}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '10px' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <img
                    src="/logo192.png"
                    alt="Default logo"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '10px' }}
                  />
                )}
              </div>

              <div>
                <h2
                  style={{
                    fontSize: '17px',
                    fontWeight: 800,
                    color: COLORS.textPrim,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    margin: '0 0 6px 0',
                  }}
                >
                  {collegeName}
                </h2>
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#fff',
                    background: COLORS.grad,
                    borderRadius: '6px',
                    padding: '3px 10px',
                    boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                  }}
                >
                  Student Portal
                </span>
              </div>
            </div>

            {/* Description */}
            <p
              style={{
                fontSize: '13.5px',
                lineHeight: 1.7,
                color: COLORS.textMute,
                margin: '0 0 24px 0',
                maxWidth: '340px',
              }}
            >
              {description}
            </p>

            {/* Social icons — inside branding column on wide screens */}
            {hasSocials && (
              <div aria-label="Social media links">
                <p
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: COLORS.textFaint,
                    margin: '0 0 12px 0',
                  }}
                >
                  Follow Us
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {activeSocials.map(({ key, label, Icon, hoverColor, ariaLabel }) => (
                    <SocialIcon
                      key={key}
                      href={socialLinks[key]}
                      ariaLabel={ariaLabel}
                      label={label}
                      hoverColor={hoverColor}
                    >
                      <Icon />
                    </SocialIcon>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Column 2: Quick Links ── */}
          <nav aria-label="Quick navigation links" className="tenant-footer-col">
            <FooterHeading>Quick Links</FooterHeading>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {QUICK_LINKS.map(({ label, path }) => (
                <li key={path}>
                  <FooterNavLink label={label} onClick={() => handleNavigate(path)} />
                </li>
              ))}
            </ul>
          </nav>

          {/* ── Column 3: Student Services ── */}
          <nav aria-label="Student services" className="tenant-footer-col">
            <FooterHeading>Student Services</FooterHeading>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {STUDENT_SERVICES.map(({ label, path }) => (
                <li key={label}>
                  <FooterNavLink label={label} onClick={() => handleNavigate(path)} />
                </li>
              ))}
            </ul>
          </nav>

          {/* ── Column 4: Contact & Support ── */}
          <div className="tenant-footer-col">
            <FooterHeading>Contact &amp; Support</FooterHeading>

            {hasContactInfo && (
              <div
                style={{
                  background: COLORS.bgCard,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '16px',
                  padding: '20px',
                  marginBottom: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                }}
              >
                {phone && (
                  <ContactRow
                    Icon={PhoneIcon}
                    href={`tel:${phone}`}
                    ariaLabel={`Call us at ${phone}`}
                  >
                    {phone}
                  </ContactRow>
                )}
                {email && (
                  <ContactRow
                    Icon={EnvelopeIcon}
                    href={`mailto:${email}`}
                    ariaLabel={`Email us at ${email}`}
                  >
                    {email}
                  </ContactRow>
                )}
                {website && (
                  <ContactRow
                    Icon={GlobeAltIcon}
                    href={website}
                    external
                    ariaLabel={`Visit the official website at ${website}`}
                  >
                    {website.replace(/^https?:\/\//, '')}
                  </ContactRow>
                )}
                {address && (
                  <ContactRow
                    Icon={MapPinIcon}
                    ariaLabel={`Address: ${address}`}
                  >
                    {address}
                  </ContactRow>
                )}
              </div>
            )}

            {/* Support Center CTA */}
            <SupportButton onClick={() => handleNavigate('/support-student')} />
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════
          BOTTOM COPYRIGHT STRIP
      ══════════════════════════════════════════ */}
      <div
        style={{
          background: COLORS.bgDeep,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <div className="tenant-footer-copyright">
          <p style={{ fontSize: '13px', color: COLORS.textFaint, margin: 0 }}>
            &copy; {currentYear}{' '}
            <span style={{ color: COLORS.textSec, fontWeight: 600 }}>{collegeName}</span>.
            {' '}All Rights Reserved.
          </p>
          <p style={{ fontSize: '12px', color: COLORS.textFaint, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <span style={{ color: COLORS.textFaint }}>The Skill Genie Platform</span>
            <span
              style={{ width: '3px', height: '3px', borderRadius: '50%', background: COLORS.textFaint, display: 'inline-block', flexShrink: 0 }}
              aria-hidden="true"
            />
            <span>
              Powered by{' '}
              <a
                href="https://evolvexinnovations.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Evolve X Innovations - opens in a new tab"
                style={{
                  color: COLORS.accent,
                  textDecoration: 'none',
                  fontWeight: 600,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                Evolve X Innovations
              </a>
            </span>
          </p>
        </div>
      </div>

      {/* ── Responsive CSS ── */}
      <style>{`
        /* Container */
        .tenant-footer-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 60px 40px 52px;
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 1.3fr;
          gap: 48px;
          align-items: flex-start;
        }

        /* Copyright strip */
        .tenant-footer-copyright {
          max-width: 1400px;
          margin: 0 auto;
          padding: 28px 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          text-align: center;
        }

        /* Logo wrapper — hover animation */
        .tenant-footer-logo-wrap {
          width: 64px;
          height: 64px;
          flex-shrink: 0;
          border-radius: 14px;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          box-shadow: 0 12px 32px -8px rgba(0,0,0,0.5);
          border: 1px solid rgba(255,255,255,0.12);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: default;
        }
        .tenant-footer-logo-wrap:hover {
          transform: translateY(-3px) scale(1.04);
          box-shadow: 0 20px 40px -8px rgba(99,102,241,0.3);
        }

        /* Tablet: 2-col */
        @media (max-width: 1100px) {
          .tenant-footer-container {
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            padding: 40px 28px 44px;
          }
          .tenant-footer-col--brand {
            grid-column: 1 / -1;
          }
        }

        /* Mobile: 1-col */
        @media (max-width: 640px) {
          .tenant-footer-container {
            grid-template-columns: 1fr;
            padding: 30px 20px 36px;
            gap: 36px;
          }
          .tenant-footer-col--brand {
            grid-column: auto;
          }
          .tenant-footer-copyright {
            padding: 24px 20px;
          }
        }
      `}</style>
    </footer>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════════════════ */

/** Section heading with bottom divider */
function FooterHeading({ children }) {
  return (
    <h3
      style={{
        fontSize: '11.5px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: COLORS.textSec,
        margin: '0 0 18px 0',
        paddingBottom: '12px',
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </h3>
  );
}

/** Navigation link with rightward hover slide */
function FooterNavLink({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={`Navigate to ${label}`}
      style={{
        background: 'none',
        border: 'none',
        padding: '7px 0',
        cursor: 'pointer',
        fontSize: '13.5px',
        fontWeight: 500,
        color: COLORS.textMute,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'color 0.2s ease, transform 0.2s ease',
        outline: 'none',
        borderRadius: '4px',
        width: '100%',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.color = '#818CF8';
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.color = COLORS.textMute;
        e.currentTarget.style.transform = 'translateX(0)';
      }}
      onFocus={e => {
        e.currentTarget.style.color = '#818CF8';
        e.currentTarget.style.boxShadow = '0 0 0 2px rgba(99,102,241,0.5)';
      }}
      onBlur={e => {
        e.currentTarget.style.color = COLORS.textMute;
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <ArrowRightIcon
        style={{ width: '12px', height: '12px', flexShrink: 0, opacity: 0.6 }}
        aria-hidden="true"
      />
      {label}
    </button>
  );
}

/** Contact info row with icon */
function ContactRow({ Icon, href, external, ariaLabel, children }) {
  const content = (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: '30px',
          height: '30px',
          borderRadius: '8px',
          background: 'rgba(99,102,241,0.1)',
          border: '1px solid rgba(99,102,241,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: '1px',
        }}
        aria-hidden="true"
      >
        <Icon style={{ width: '15px', height: '15px', color: '#818CF8' }} />
      </div>
      <span
        style={{
          fontSize: '13px',
          color: COLORS.textSec,
          lineHeight: 1.5,
          fontWeight: 400,
          wordBreak: 'break-word',
        }}
      >
        {children}
      </span>
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        aria-label={ariaLabel}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        style={{ textDecoration: 'none', display: 'block', transition: 'opacity 0.2s' }}
        onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
      >
        {content}
      </a>
    );
  }
  return <div aria-label={ariaLabel}>{content}</div>;
}

/** Support Center pill button */
function SupportButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Open Support Center"
      style={{
        width: '100%',
        background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: '50px',
        padding: '13px 20px',
        fontSize: '13px',
        fontWeight: 700,
        cursor: 'pointer',
        letterSpacing: '0.02em',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        boxShadow: '0 4px 16px rgba(99,102,241,0.25)',
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.03)';
        e.currentTarget.style.boxShadow = '0 8px 28px rgba(99,102,241,0.45)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.25)';
      }}
      onFocus={e => {
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.5), 0 4px 16px rgba(99,102,241,0.25)';
      }}
      onBlur={e => {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.25)';
      }}
    >
      Support Center
    </button>
  );
}

/** Circular social media icon button */
function SocialIcon({ href, ariaLabel, label, hoverColor, children }) {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${ariaLabel} (opens in new tab)`}
      title={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: hovered
          ? hoverColor
          : 'rgba(255,255,255,0.07)',
        border: `1px solid ${hovered ? 'transparent' : COLORS.border}`,
        color: hovered ? '#fff' : COLORS.textSec,
        textDecoration: 'none',
        transition: 'background 0.3s ease, transform 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered ? `0 8px 20px ${hoverColor}55` : 'none',
        outline: 'none',
        flexShrink: 0,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      {children}
    </a>
  );
}

export default TenantFooter;
